/**
 * UTILITAIRES CSV
 * Fonctions réutilisables pour l'export/import CSV avec Papa Parse
 */

import Papa from 'papaparse';

/**
 * Cellules texte que l'export préfixe d'une apostrophe (anti-injection de formules).
 * Les réponses et libellés publics restent du texte, même après des espaces.
 * Partagée avec l'import, qui retire ce préfixe (unescapeCSVFormula).
 */
export const CSV_FORMULA_PATTERN = /^[\s\uFEFF]*[=+\-@\t\r\n]/;

/**
 * Inverse de l'échappement de l'export : retire l'apostrophe initiale
 * seulement si le reste aurait été échappé (`'abc` reste `'abc`).
 * Limite : une valeur saisie qui commence déjà par `'` suivie d'un déclencheur
 * (`'=x`) perd son apostrophe au réimport.
 */
export function unescapeCSVFormula<T>(value: T): T | string {
  if (
    typeof value === 'string' &&
    value.startsWith("'") &&
    CSV_FORMULA_PATTERN.test(value.slice(1))
  ) {
    return value.slice(1);
  }
  return value;
}

/**
 * Exporte des données en CSV et télécharge le fichier
 *
 * @param options.silentEmpty Si true, ne pas afficher d'alert sur data vide.
 *   L'appelant doit alors gérer le messaging (toast, etc.) en amont.
 *   Utile quand l'appelant a déjà vérifié qu'il y avait des éléments
 *   sélectionnés et veut distinguer "0 résultats légitimes" d'un fail
 *   technique (catch) sans accumuler deux notifications.
 */
export function exportToCSV<TData extends Record<string, any>>(
  data: TData[],
  filename: string,
  options?: {
    columns?: Array<{ key: keyof TData; dataKey?: string; label: string }>;
    dateFields?: Array<keyof TData>;
    visibleColumns?: string[]; // IDs des colonnes visibles dans le tableau
    transformers?: Record<string, (value: any, item?: TData) => string>; // Transformateurs personnalisés par champ
    silentEmpty?: boolean;
  }
) {
  if (data.length === 0) {
    if (!options?.silentEmpty) {
      alert('Aucune donnée à exporter');
    }
    return;
  }

  // Colonnes techniques à exclure systématiquement
  const EXCLUDED_COLUMNS = ['id', 'select', 'actions', 'created_at', 'updated_at'];

  // Préparer les données pour l'export
  const exportData = data.map((item) => {
    const row: Record<string, any> = {};

    if (options?.columns) {
      // Si des colonnes sont spécifiées, n'exporter que celles-ci
      options.columns.forEach(({ key, dataKey, label }) => {
        const keyStr = String(key);

        // Exclure les colonnes techniques
        if (EXCLUDED_COLUMNS.includes(keyStr)) {
          return;
        }

        // Vérifier si la colonne est visible (si visibleColumns est fourni et non vide)
        if (
          options.visibleColumns &&
          options.visibleColumns.length > 0 &&
          !options.visibleColumns.includes(keyStr)
        ) {
          return;
        }

        // Utiliser dataKey pour accéder aux données si disponible, sinon key
        const dataAccessKey = dataKey || key;
        let value = item[dataAccessKey as keyof TData];

        // Appliquer le transformateur personnalisé si disponible (indexé par key, pas dataKey)
        if (options.transformers && options.transformers[keyStr]) {
          value = options.transformers[keyStr](value, item) as any;
        }
        // Sinon, formater les dates au format français si spécifié
        else if (options.dateFields?.includes(key) && value) {
          const dateValue = typeof value === 'string' ? value : String(value);
          value = new Date(dateValue).toLocaleDateString('fr-FR') as any;
        }

        row[label] = value ?? '';
      });
    } else {
      // Sinon, exporter toutes les clés (sauf celles exclues)
      Object.keys(item).forEach((key) => {
        // Exclure les colonnes techniques
        if (EXCLUDED_COLUMNS.includes(key)) {
          return;
        }

        // Vérifier si la colonne est visible (si visibleColumns est fourni et non vide)
        if (
          options?.visibleColumns &&
          options.visibleColumns.length > 0 &&
          !options.visibleColumns.includes(key)
        ) {
          return;
        }

        let value = item[key];

        // Appliquer le transformateur personnalisé si disponible
        if (options?.transformers && options.transformers[key]) {
          value = options.transformers[key](value, item);
        }
        // Sinon, formater les dates au format français si spécifié
        else if (options?.dateFields?.includes(key as keyof TData) && value) {
          value = new Date(value).toLocaleDateString('fr-FR');
        }

        row[key] = value ?? '';
      });
    }

    return row;
  });

  // Générer le CSV
  const csv = Papa.unparse(exportData, {
    delimiter: ';', // Séparateur pour Excel français
    header: true,
    escapeFormulae: CSV_FORMULA_PATTERN,
  });

  // Créer un Blob et télécharger
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM pour Excel
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Parse un fichier CSV et retourne les données
 */
export function parseCSV<TData>(
  file: File,
  options?: {
    columns?: Array<{ key: keyof TData; label: string }>;
    transform?: (row: Record<string, any>) => TData;
  }
): Promise<TData[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      delimiter: ';',
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const data = results.data as Record<string, any>[];

          if (options?.transform) {
            // Transformer chaque ligne avec la fonction fournie
            const transformed = data.map(options.transform);
            resolve(transformed);
          } else {
            resolve(data as TData[]);
          }
        } catch (error) {
          reject(error);
        }
      },
      error: (error) => {
        reject(error);
      },
    });
  });
}

/**
 * Déclenche la sélection d'un fichier CSV
 */
export function selectCSVFile(): Promise<File> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';

    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        resolve(file);
      } else {
        reject(new Error('Aucun fichier sélectionné'));
      }
    };

    input.click();
  });
}
